import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';

interface TransactionRecord {
  codigoComercio: string;
  referencia: string;
  numeroLote: string;
  tipoOperacion: string;
  numeroTarjeta: string;
  tipoTarjeta: string;
  fechaTransaccion: string;
  fechaProceso: string;
  fechaAbono: string;
  importeTransaccion: number;
  descuentoMovimiento: number;
  comisionVisa: number;
  comisionIgv: number;
  importeNeto: number;
  tipoCaptura: string;
  estadoAbono: string;
  cuentaBancaria: string;
  codigoAutorizacion: string;
  idUnicoTransaccion: string;
  numeroSerieTerminal: string;
  numeroCuota: number;
  importeCashback: number;
  espaciosBlanco: string;
  idUnicoTransaccion2: string;
}

@Component({ standalone: false,
  selector: 'app-izipay-generator',
  templateUrl: './izipay-generator.html',
  styleUrls: ['./izipay-generator.css']
})
export class IzipayGeneratorComponent implements OnInit {
  apiUrl: string = 'https://izipay-file-service-858281476358.us-east1.run.app';
  fileName: string = 'PMP_VM_20536557858_2_20240129.txt';
  records: TransactionRecord[] = [];
  isLoading: boolean = false;
  isLoadingExcel: boolean = false;
  isLoadingPreview: boolean = false;
  statusMessage: string = '';
  statusClass: string = '';
  excelFileName: string = '';
  /** Tokens (uno por línea) para cargar desde API */
  tokensText: string = '';
  /** Tokens usados en la última carga (API o Excel con Token) para descargar TXT desde el backend */
  lastTokens: string[] = [];

  /** Nombres posibles de columnas del Excel "Formato carga masiva" -> campo del modelo */
  private static EXCEL_COLUMN_MAP: Record<string, keyof TransactionRecord> = {
    'Cód. Comercio': 'codigoComercio',
    'Cod. Comercio': 'codigoComercio',
    'Referencia': 'referencia',
    'Nro. Lote': 'numeroLote',
    'Tipo Op.': 'tipoOperacion',
    'Nro. Tarjeta': 'numeroTarjeta',
    'Tipo Tarjeta': 'tipoTarjeta',
    'Fecha Transac.': 'fechaTransaccion',
    'Fecha Proceso': 'fechaProceso',
    'Fecha Abono': 'fechaAbono',
    'Importe Transac.': 'importeTransaccion',
    'Desc. Movimiento': 'descuentoMovimiento',
    'Comisión Visa': 'comisionVisa',
    'Comision Visa': 'comisionVisa',
    'Comisión IGV': 'comisionIgv',
    'Comision IGV': 'comisionIgv',
    'Importe Neto': 'importeNeto',
    'Tipo Captura': 'tipoCaptura',
    'Estado Abono': 'estadoAbono',
    'Cuenta Bancaria': 'cuentaBancaria',
    'Cód. Autoriz.': 'codigoAutorizacion',
    'Cod. Autoriz.': 'codigoAutorizacion',
    'ID Único Transac.': 'idUnicoTransaccion',
    'ID Unico Transac.': 'idUnicoTransaccion',
    'Nro. Serie Terminal': 'numeroSerieTerminal',
    'Nro. Cuota': 'numeroCuota',
    'Importe Cashback': 'importeCashback',
    'Espacios Blanco': 'espaciosBlanco',
    'ID Único Transac. 2': 'idUnicoTransaccion2',
    'ID Unico Transac. 2': 'idUnicoTransaccion2'
  };

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.records = [];
  }

  /** Extrae tokens del textarea (uno por línea, sin vacíos). */
  getTokensFromText(): string[] {
    if (!this.tokensText || !this.tokensText.trim()) return [];
    return this.tokensText
      .split(/\r?\n/)
      .map(t => t.trim())
      .filter(t => t.length > 0);
  }

  /** Llama al endpoint preview de la API y reemplaza la tabla con los registros devueltos. */
  async loadFromApi(tokensFromExcel?: string[]) {
    const tokens = (tokensFromExcel && tokensFromExcel.length > 0) ? tokensFromExcel : this.getTokensFromText();
    if (tokens.length === 0) {
      this.showStatus('error', 'Ingresá al menos un token (uno por línea).');
      return;
    }
    const url = `${this.apiUrl.replace(/\/$/, '')}/izipay-batch/preview`;
    this.isLoadingPreview = true;
    this.statusMessage = '';
    try {
      const response = await firstValueFrom(
        this.http.post<{ lines: string[]; records: TransactionRecord[]; errors: string[]; generatedCount: number }>(
          url,
          { tokens }
        )
      );
      this.records = response.records ?? [];
      if (this.records.length > 0) {
        this.lastTokens = tokens;
        this.showStatus(
          'success',
          `Se cargaron ${this.records.length} registro(s) desde la API.` +
            (response.errors?.length ? ` Advertencias: ${response.errors.length}.` : '')
        );
      }
      if (response.errors?.length) {
        this.showStatus(
          'info',
          response.errors.slice(0, 3).join(' ') + (response.errors.length > 3 ? '...' : '')
        );
      }
      if (this.records.length === 0 && response.errors?.length) {
        this.showStatus('error', response.errors[0] ?? 'No se generaron registros.');
      }
      if (this.records.length === 0) this.lastTokens = [];
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'error' in err
        ? (err as { error?: { message?: string } }).error?.message
        : err instanceof Error ? err.message : 'Error al llamar a la API';
      this.showStatus('error', String(msg));
    } finally {
      this.isLoadingPreview = false;
    }
  }

  /** Convierte fecha DDMMYYYY (string o number) a YYYY-MM-DD */
  private static dateDdmmyyyyToIso(val: unknown): string {
    if (val == null || val === '') return '';
    const s = String(val).replace(/\s/g, '');
    if (s.length !== 8) return String(val);
    const dd = s.slice(0, 2);
    const mm = s.slice(2, 4);
    const yyyy = s.slice(4, 8);
    return `${yyyy}-${mm}-${dd}`;
  }

  addRecord() {
    const newRecord: TransactionRecord = {
      codigoComercio: '',
      referencia: '',
      numeroLote: '',
      tipoOperacion: '',
      numeroTarjeta: '',
      tipoTarjeta: '',
      fechaTransaccion: '',
      fechaProceso: '',
      fechaAbono: '',
      importeTransaccion: 0,
      descuentoMovimiento: 0,
      comisionVisa: 0,
      comisionIgv: 0,
      importeNeto: 0,
      tipoCaptura: '',
      estadoAbono: '',
      cuentaBancaria: '',
      codigoAutorizacion: '',
      idUnicoTransaccion: '',
      numeroSerieTerminal: '',
      numeroCuota: 0,
      importeCashback: 0,
      espaciosBlanco: '',
      idUnicoTransaccion2: ''
    };
    this.records.push(newRecord);
  }

  async onExcelSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    this.excelFileName = file.name;
    this.isLoadingExcel = true;
    this.statusMessage = '';
    input.value = '';
    try {
      const buffer = await file.arrayBuffer();
      const data = new Uint8Array(buffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
      await this.simulateServiceCall();
      let parsed = this.parseFormatoCargaMasiva(rows);
      const fromTokenColumnOnly = parsed.length === 0;
      if (parsed.length === 0) {
        parsed = this.parseExcelConColumnaToken(rows);
      }
      if (parsed.length > 0) {
        if (fromTokenColumnOnly) {
          const tokens = parsed.map(r => String(r.referencia || '').trim()).filter(Boolean);
          this.isLoadingExcel = false;
          await this.loadFromApi(tokens);
        } else {
          this.records = parsed;
          this.showStatus('success', `Se cargaron ${parsed.length} registro(s) desde el Excel.`);
        }
      } else {
        this.showStatus('info', 'No se encontraron filas válidas. Use el formato de carga masiva o un Excel con columna "Token".');
      }
    } catch (err) {
      console.error('Error al leer Excel:', err);
      this.showStatus('error', 'Error al leer el archivo Excel.');
    } finally {
      this.isLoadingExcel = false;
    }
  }

  /** Simula la espera de una llamada al servicio (1,5 s) */
  private simulateServiceCall(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 1500));
  }

  /** Fallback: Excel que solo tiene columna Token -> un registro por fila con referencia = token */
  private parseExcelConColumnaToken(rows: Record<string, unknown>[]): TransactionRecord[] {
    const result: TransactionRecord[] = [];
    for (const row of rows) {
      const token = this.getCellByTokenKey(row);
      const ref = token != null ? String(token).trim() : '';
      if (ref) {
        result.push(this.recordFromReferencia(ref));
      }
    }
    return result;
  }

  /** Obtiene el valor de la celda Token probando varias claves (Token, "Token", con BOM, etc.) */
  private getCellByTokenKey(row: Record<string, unknown>): unknown {
    const v = row['Token'] ?? row['token'] ?? row['TOKEN'];
    if (v !== undefined && v !== null && v !== '') return v;
    const tokenKey = Object.keys(row).find(k => k.trim().toLowerCase().replace(/^["']|["']$/g, '') === 'token');
    return tokenKey !== undefined ? row[tokenKey] : undefined;
  }

  /** Crea un registro con solo la referencia (token); el resto lo devuelve la API. */
  private recordFromReferencia(referencia: string): TransactionRecord {
    return this.normalizeRecord({ referencia });
  }

  /** Parsea filas del Excel "Formato carga masiva" a TransactionRecord[] */
  private parseFormatoCargaMasiva(rows: Record<string, unknown>[]): TransactionRecord[] {
    const dateFields: (keyof TransactionRecord)[] = ['fechaTransaccion', 'fechaProceso', 'fechaAbono'];
    const numFields: (keyof TransactionRecord)[] = [
      'importeTransaccion', 'descuentoMovimiento', 'comisionVisa', 'comisionIgv', 'importeNeto',
      'numeroCuota', 'importeCashback'
    ];
    const map = IzipayGeneratorComponent.EXCEL_COLUMN_MAP;
    const result: TransactionRecord[] = [];
    for (const row of rows) {
      const record: Record<string, unknown> = {};
      for (const excelKey of Object.keys(row)) {
        const field = map[excelKey] ?? map[excelKey.trim()];
        if (!field) continue;
        let val = row[excelKey];
        if (dateFields.includes(field)) {
          val = IzipayGeneratorComponent.dateDdmmyyyyToIso(val);
        } else if (numFields.includes(field) && val !== '' && val !== null && val !== undefined) {
          const n = Number(val);
          val = isNaN(n) ? val : n;
        } else if (typeof val === 'number' && !numFields.includes(field)) {
          val = String(val);
        }
        record[field] = val ?? '';
      }
      const hasData = Object.values(record).some(v => v !== '' && v !== undefined && v !== null);
      if (hasData) {
        result.push(this.normalizeRecord(record as unknown as Partial<TransactionRecord>));
      }
    }
    return result;
  }

  private normalizeRecord(partial: Partial<TransactionRecord>): TransactionRecord {
    return {
      codigoComercio: String(partial.codigoComercio ?? ''),
      referencia: String(partial.referencia ?? ''),
      numeroLote: String(partial.numeroLote ?? ''),
      tipoOperacion: String(partial.tipoOperacion ?? ''),
      numeroTarjeta: String(partial.numeroTarjeta ?? ''),
      tipoTarjeta: String(partial.tipoTarjeta ?? ''),
      fechaTransaccion: String(partial.fechaTransaccion ?? ''),
      fechaProceso: String(partial.fechaProceso ?? ''),
      fechaAbono: String(partial.fechaAbono ?? ''),
      importeTransaccion: Number(partial.importeTransaccion) || 0,
      descuentoMovimiento: Number(partial.descuentoMovimiento) || 0,
      comisionVisa: Number(partial.comisionVisa) || 0,
      comisionIgv: Number(partial.comisionIgv) || 0,
      importeNeto: Number(partial.importeNeto) || 0,
      tipoCaptura: String(partial.tipoCaptura ?? ''),
      estadoAbono: String(partial.estadoAbono ?? ''),
      cuentaBancaria: String(partial.cuentaBancaria ?? ''),
      codigoAutorizacion: String(partial.codigoAutorizacion ?? ''),
      idUnicoTransaccion: String(partial.idUnicoTransaccion ?? ''),
      numeroSerieTerminal: String(partial.numeroSerieTerminal ?? ''),
      numeroCuota: Number(partial.numeroCuota) || 0,
      importeCashback: Number(partial.importeCashback) || 0,
      espaciosBlanco: String(partial.espaciosBlanco ?? ''),
      idUnicoTransaccion2: String(partial.idUnicoTransaccion2 ?? '')
    };
  }

  clearAll() {
    if (confirm('¿Estás seguro de que quieres limpiar todos los registros?')) {
      this.records = [];
      this.lastTokens = [];
      this.excelFileName = '';
      this.showStatus('info', 'Todos los registros han sido eliminados');
    }
  }

  showStatus(type: string, message: string) {
    this.statusClass = `status ${type}`;
    this.statusMessage = message;
    setTimeout(() => {
      this.statusMessage = '';
    }, 5000);
  }

  getDateTimeValue(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toISOString().slice(0, 16);
  }

  getDateValue(date: string): string {
    if (!date) return '';
    return date;
  }

  loadExampleData() {
    const exampleRecords: TransactionRecord[] = [
      {
        codigoComercio: "008T07099",
        referencia: "REF001",
        numeroLote: "LOTE001",
        tipoOperacion: "VENTA",
        numeroTarjeta: "2512445339584344",
        tipoTarjeta: "Crédito",
        fechaTransaccion: "2024-01-19",
        fechaProceso: "2024-01-30",
        fechaAbono: "2024-01-31",
        importeTransaccion: 120.0,
        descuentoMovimiento: 0,
        comisionVisa: 2.5,
        comisionIgv: 0.45,
        importeNeto: 117.05,
        tipoCaptura: "MANUAL",
        estadoAbono: "PENDIENTE",
        cuentaBancaria: "1234567890",
        codigoAutorizacion: "AUTH001",
        idUnicoTransaccion: "TXN001",
        numeroSerieTerminal: "TERM001",
        numeroCuota: 1,
        importeCashback: 0,
        espaciosBlanco: "",
        idUnicoTransaccion2: "TXN001"
      },
      {
        codigoComercio: "008T07098",
        referencia: "REF002",
        numeroLote: "LOTE001",
        tipoOperacion: "VENTA",
        numeroTarjeta: "2512445339584344",
        tipoTarjeta: "Débito",
        fechaTransaccion: "2024-01-19",
        fechaProceso: "2024-01-30",
        fechaAbono: "2024-01-31",
        importeTransaccion: 508.0,
        descuentoMovimiento: 0,
        comisionVisa: 5.0,
        comisionIgv: 0.9,
        importeNeto: 502.1,
        tipoCaptura: "MANUAL",
        estadoAbono: "PENDIENTE",
        cuentaBancaria: "1234567890",
        codigoAutorizacion: "AUTH002",
        idUnicoTransaccion: "TXN002",
        numeroSerieTerminal: "TERM001",
        numeroCuota: 1,
        importeCashback: 0,
        espaciosBlanco: "",
        idUnicoTransaccion2: "TXN002"
      }
    ];

    this.clearAll();
    this.records = exampleRecords;
  }

  /** Convierte fecha YYYY-MM-DD a DDMMYYYY para el txt F5 */
  private static toDdmmyyyy(dateStr: string): string {
    if (!dateStr || dateStr.trim() === '') return '';
    const s = dateStr.trim();
    if (s.length === 10 && s[4] === '-' && s[7] === '-') {
      return s.slice(8, 10) + s.slice(5, 7) + s.slice(0, 4);
    }
    return s;
  }

  /** Fecha YYYY-MM-DD a DDMMYYYYHHMISS (14 chars), sin hora = DDMMYYYY + 000000 */
  private static toDdmmyyyyHhmiss(dateStr: string): string {
    const d = IzipayGeneratorComponent.toDdmmyyyy(dateStr);
    return (d.slice(0, 8) + '000000').slice(0, 14);
  }

  private static padRight(s: string, len: number): string {
    return (s || '').slice(0, len).padEnd(len, ' ');
  }

  private static padLeft(s: string, len: number): string {
    const str = (s || '').trim();
    return str.slice(-len).padStart(len, ' ');
  }

  /** Número F5: 2 decimales, rellenado a la izquierda con espacios (12 chars = "       10.00") */
  private static numF5Left(val: unknown, totalLen: number): string {
    if (val === undefined || val === null || val === '') return ' '.repeat(totalLen - 3) + '0.00';
    const num = Number(val);
    const str = isNaN(num) ? '0.00' : num.toFixed(2);
    return str.padStart(totalLen, ' ');
  }

  /** Número con 2 decimales, rellenado a la izquierda con ceros (ej: 10 chars = 0000000.00) */
  private static numF5(val: unknown, totalLen: number): string {
    if (val === undefined || val === null || val === '') return '0'.repeat(totalLen - 3) + '0.00';
    const num = Number(val);
    const str = isNaN(num) ? '0.00' : num.toFixed(2);
    return str.padStart(totalLen, '0');
  }

  /** Formato F5 según Origen de los datos: 266 caracteres por línea (igual que el backend). */
  private recordToF5Line(record: TransactionRecord): string {
    const d = (v: unknown) => (v === undefined || v === null ? '' : String(v).trim());
    const ddmmyyyy = (s: string) => IzipayGeneratorComponent.toDdmmyyyy(s);
    const ddmmyyyyHhmiss = (s: string) => IzipayGeneratorComponent.toDdmmyyyyHhmiss(s);
    const padR = (s: string, n: number) => IzipayGeneratorComponent.padRight(s, n);
    const padL = (s: string, n: number) => IzipayGeneratorComponent.padLeft(s, n);
    const numLeft = (v: unknown, n: number) => IzipayGeneratorComponent.numF5Left(v, n);
    const num = (v: unknown, n: number) => IzipayGeneratorComponent.numF5(v, n);

    const codigoComercio = (d(record.codigoComercio) || '').replace(/\D/g, '').slice(-9).padStart(9, '0');
    const referencia = padR(d(record.referencia), 12);
    const numeroLote = padL(d(record.numeroLote), 12);
    const tipoOperacion = padL(d(record.tipoOperacion), 4);
    const numeroTarjeta = padR(d(record.numeroTarjeta), 19);
    const tipoTarjeta = padR(d(record.tipoTarjeta).slice(0, 3), 3);
    const fechaTransaccion = padR(ddmmyyyyHhmiss(record.fechaTransaccion), 14);
    const fechaProceso = padR(ddmmyyyy(record.fechaProceso), 8);
    const fechaAbono = padR(ddmmyyyy(record.fechaAbono), 8);
    const unique15 = padR(padL(d(record.idUnicoTransaccion), 15), 15);
    const unique16 = padR(padL(d(record.idUnicoTransaccion2), 16), 16);

    return (
      codigoComercio +
      referencia +
      numeroLote +
      tipoOperacion +
      numeroTarjeta +
      tipoTarjeta +
      fechaTransaccion +
      fechaProceso +
      fechaAbono +
      numLeft(record.importeTransaccion, 12) +
      numLeft(record.descuentoMovimiento, 12) +
      numLeft(record.comisionVisa, 12) +
      numLeft(record.comisionIgv, 12) +
      numLeft(record.importeNeto, 12) +
      padR(d(record.tipoCaptura).slice(0, 1), 1) +
      padR((d(record.estadoAbono) || '1').slice(0, 1), 1) +
      padR(d(record.cuentaBancaria), 20) +
      padR(d(record.codigoAutorizacion), 6) +
      unique15 +
      padR(d(record.numeroSerieTerminal), 11) +
      String(Math.floor(Number(record.numeroCuota) || 0)).padStart(2, '0').slice(-2) +
      num(record.importeCashback, 10) +
      padR(d(record.espaciosBlanco), 35) +
      unique16
    );
  }

  exportF5() {
    const tokens = this.getTokensFromText().length > 0 ? this.getTokensFromText() : this.lastTokens;
    if (tokens.length === 0) {
      this.showStatus('error', 'Para exportar F5 el backend necesita tokens. Cargá un Excel con columna Token o ingresá tokens en el área de texto.');
      return;
    }
    this.downloadTxtFromBackend(tokens);
  }

  /** Descarga el TXT F5 desde el backend (la verdad absoluta está en el servidor). */
  private async downloadTxtFromBackend(tokens: string[]) {
    this.isLoading = true;
    this.showStatus('info', 'Generando archivo en el servidor...');
    const url = `${this.apiUrl.replace(/\/$/, '')}/izipay-batch/generate`;
    const fileName = this.fileName?.trim() || 'PMP_VM_20536557858_2_20240129.txt';
    const finalName = fileName.endsWith('.txt') ? fileName : fileName + '.txt';
    try {
      const response = await firstValueFrom(
        this.http.post(url, { fileName: finalName, tokens }, { responseType: 'blob' })
      );
      const blob = new Blob([response], { type: 'text/plain;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      this.showStatus('success', `Archivo ${finalName} descargado desde el servidor.`);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'error' in err
        ? (err as { error?: { message?: string } }).error?.message
        : err instanceof Error ? err.message : 'Error al descargar desde el servidor';
      this.showStatus('error', String(msg));
    } finally {
      this.isLoading = false;
    }
  }

}